import React, { useEffect, useMemo, useState } from "react";
import { CrewPilotModuleShell } from "./CrewPilotModuleShell";
import { PAGE_LAYOUT, PAGE_LAYOUT_CLASS, pageLayoutClass } from "../layout/pageLayout";
import {
  formatClientCurrency,
  formatClientHours,
  formatOpenInvoiceCountLabel,
  loadClientIntelligence,
} from "../helpers/clientIntelligence";
import { ClientStatementScreen } from "./accounts-receivable/ClientStatementScreen";
import { InvoiceListCard } from "./invoice/InvoiceListCard";
import { isClientArchived } from "../helpers/clientVisibility";
import { usePermissions } from "../hooks/usePermissions";
import { ACTION_BTN_SM } from "./estimate/estimateStyles";
import {
  HUB_STATUS_PILL_ACTIVE_GOLD_STYLE,
  HUB_STATUS_PILL_BTN_BASE_STYLE,
} from "./shared/hubFilterPillStyles";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { ClientDesktopDetail } from "./ClientDesktopDetail";
import "./ClientIntelligenceScreen.css";

const MOBILE_TABS = [
  { id: "snapshot", label: "Snapshot" },
  { id: "projects", label: "Projects" },
  { id: "financial", label: "Financial" },
  { id: "activity", label: "Activity" },
];

const ARCHIVED_BADGE_STYLE = {
  display: "inline-block",
  marginTop: "8px",
  padding: "4px 8px",
  borderRadius: "8px",
  border: "1px solid rgba(201,168,76,0.35)",
  background: "rgba(201,168,76,0.12)",
  color: "rgba(240,230,204,0.92)",
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

function MetricTile({ label, value, hint, onClick }) {
  const interactive = typeof onClick === "function";
  const Tag = interactive ? "button" : "div";

  return (
    <Tag
      type={interactive ? "button" : undefined}
      className={pageLayoutClass(
        "client-intel__metric",
        interactive && "client-intel__metric--action",
      )}
      onClick={onClick}
    >
      <div className="client-intel__metric-label">{label}</div>
      <div className="client-intel__metric-value">{value}</div>
      {hint ? <div className="client-intel__metric-hint">{hint}</div> : null}
    </Tag>
  );
}

function ProjectRow({ project, onOpen }) {
  return (
    <button
      type="button"
      className="client-intel__row"
      onClick={() => onOpen?.(project.raw || project)}
    >
      <div>
        <div className="client-intel__row-title">{project.name}</div>
        <div className="client-intel__row-meta">
          {project.projectNumber ? `#${project.projectNumber}` : "No project #"}
          {project.siteAddress ? ` · ${project.siteAddress}` : ""}
        </div>
        <span
          className={pageLayoutClass(
            "client-intel__badge",
            !project.isActive && "client-intel__badge--muted",
          )}
        >
          {project.isActive ? "Active" : "Completed"}
        </span>
      </div>
      <div className="client-intel__row-aside">
        {project.value != null ? formatClientCurrency(project.value) : "—"}
      </div>
    </button>
  );
}

function invoiceForCard(invoice, projects) {
  const named = [
    ...(projects?.active || []),
    ...(projects?.completed || []),
    ...(projects?.all || []),
  ];
  const project = named.find((row) => row.projectId === invoice.project_id);
  const title = String(invoice.title || "").trim() || project?.name || "";
  if (title === (invoice.title || "")) return invoice;
  return { ...invoice, title };
}

function ActivityRow({ item, onOpenProject, onOpenInvoice }) {
  const handleClick = () => {
    if (item.type === "project") {
      onOpenProject?.({ project_id: item.id, project_name: item.label });
      return;
    }
    if (item.type === "invoice") {
      onOpenInvoice?.({ invoice_id: item.id });
    }
  };

  const interactive = item.type === "project" || item.type === "invoice";

  return (
    <button
      type="button"
      className="client-intel__row"
      onClick={interactive ? handleClick : undefined}
      disabled={!interactive}
      style={interactive ? undefined : { cursor: "default" }}
    >
      <div>
        <div className="client-intel__row-title">{item.label}</div>
        <div className="client-intel__row-meta">{item.detail}</div>
      </div>
      <div className="client-intel__row-aside">
        {item.at ? new Date(item.at).toLocaleDateString() : ""}
      </div>
    </button>
  );
}

function IdentityBlock({ client, canEdit, onEdit, onViewProjects }) {
  const archived = isClientArchived(client);
  const addressLine = [client?.address, client?.city, client?.province, client?.postal_code]
    .filter(Boolean)
    .join(", ");

  return (
    <section className="cp-page-section">
      <h2 className="cp-page-section__title">Client</h2>
      <div className="cp-page-section__body">
        <h3 className="client-intel__identity-name">
          {client?.client_name || "Unnamed Client"}
        </h3>
        <div className="client-intel__identity-meta">
          {client?.contact_name ? <div>Contact: {client.contact_name}</div> : null}
          {client?.phone ? <div>Phone: {client.phone}</div> : null}
          {client?.email ? <div>Email: {client.email}</div> : null}
          {addressLine ? <div>{addressLine}</div> : null}
          {archived ? <div style={ARCHIVED_BADGE_STYLE}>Archived</div> : null}
        </div>
        <div className="client-intel__actions">
          {canEdit ? (
            <button type="button" style={ACTION_BTN_SM} onClick={onEdit}>
              Edit Client
            </button>
          ) : null}
          <button type="button" style={ACTION_BTN_SM} onClick={onViewProjects}>
            View Projects
          </button>
        </div>
      </div>
    </section>
  );
}

function SnapshotMetrics({ snapshot, onFocusProjects, onFocusFinancial, onOpenTimeLogs }) {
  const { metrics, flags } = snapshot;
  const tiles = [
    {
      key: "active",
      label: "Active Projects",
      value: String(metrics.activeProjectCount),
      hint: metrics.completedProjectCount
        ? `${metrics.completedProjectCount} completed`
        : "Current work",
      onClick: onFocusProjects,
    },
  ];

  if (metrics.activeProjectValue != null) {
    tiles.push({
      key: "value",
      label: "Active Project Value",
      value: formatClientCurrency(metrics.activeProjectValue),
      hint: "Sum of project budgets",
      onClick: onFocusProjects,
    });
  }

  if (flags.includeHours && metrics.totalHours != null) {
    tiles.push({
      key: "hours",
      label: "Hours Logged",
      value: formatClientHours(metrics.totalHours),
      hint: "From project time logs",
      onClick: onOpenTimeLogs,
    });
  }

  if (flags.includeFinancial && metrics.outstandingBalance != null) {
    tiles.push({
      key: "ar",
      label: metrics.outstandingBalance > 0 ? "Outstanding" : "Receivables",
      value:
        metrics.outstandingBalance > 0
          ? formatClientCurrency(metrics.outstandingBalance)
          : "Up to date",
      hint: !metrics.hasInvoiceHistory
        ? "No invoices yet"
        : metrics.overdueInvoiceCount > 0
          ? `${formatOpenInvoiceCountLabel(metrics.openInvoiceCount)} · ${formatClientCurrency(metrics.overdueAmount)} overdue`
          : formatOpenInvoiceCountLabel(metrics.openInvoiceCount),
      onClick: onFocusFinancial,
    });
  }

  return (
    <div className="cp-page-metrics" data-testid="client-intel-metrics">
      {tiles.map(({ key, ...tile }) => (
        <MetricTile key={key} {...tile} />
      ))}
    </div>
  );
}

function ProjectsSection({ snapshot, onOpenProject, showCompleted }) {
  const active = snapshot.projects.active;
  const completed = snapshot.projects.completed;

  return (
    <section className="cp-page-section" data-testid="client-intel-projects">
      <h2 className="cp-page-section__title">Projects</h2>
      <div className="cp-page-section__body cp-page-data-surface">
        {active.length === 0 ? (
          <p className="client-intel__empty">No active projects for this client.</p>
        ) : (
          active.map((project) => (
            <ProjectRow
              key={project.projectId}
              project={project}
              onOpen={onOpenProject}
            />
          ))
        )}

        {showCompleted && completed.length > 0 ? (
          <>
            <h3 className="cp-page-section__title" style={{ marginTop: 8 }}>
              Completed / Historical
            </h3>
            {completed.map((project) => (
              <ProjectRow
                key={project.projectId}
                project={project}
                onOpen={onOpenProject}
              />
            ))}
          </>
        ) : null}
      </div>
    </section>
  );
}

function FinancialSection({ snapshot, onOpenInvoice, onOpenStatement }) {
  if (!snapshot.flags.includeFinancial) {
    return (
      <section className="cp-page-section">
        <h2 className="cp-page-section__title">Financial</h2>
        <p className="client-intel__empty">
          Invoice access is required to view receivables for this client.
        </p>
      </section>
    );
  }

  const { metrics, invoices } = snapshot;
  const upToDate = metrics.accountUpToDate;

  return (
    <section className="cp-page-section" data-testid="client-intel-financial">
      <h2 className="cp-page-section__title">Accounts Receivable</h2>
      <div className="cp-page-section__body cp-page-data-surface">
        {upToDate ? (
          <div className="client-intel__positive">
            {metrics.hasInvoiceHistory
              ? "Account up to date — no outstanding invoices."
              : "No invoices on file for this client yet."}
          </div>
        ) : (
          <div className="client-intel__identity-meta">
            <div>
              Outstanding: {formatClientCurrency(metrics.outstandingBalance)}
            </div>
            {metrics.overdueInvoiceCount > 0 ? (
              <div>
                Overdue: {formatClientCurrency(metrics.overdueAmount)} (
                {metrics.overdueInvoiceCount})
              </div>
            ) : null}
          </div>
        )}

        {typeof onOpenStatement === "function" ? (
          <button
            type="button"
            style={{ ...ACTION_BTN_SM, marginTop: "12px" }}
            onClick={onOpenStatement}
          >
            Statement of Account
          </button>
        ) : null}

        {invoices.open.length > 0 ? (
          <>
            <h3 className="cp-page-section__title">Open invoices</h3>
            {invoices.open.map((invoice) => {
              const cardInvoice = invoiceForCard(invoice, snapshot.projects);
              return (
                <InvoiceListCard
                  key={invoice.invoice_id}
                  invoice={cardInvoice}
                  showClient={false}
                  onOpen={onOpenInvoice}
                />
              );
            })}
          </>
        ) : null}

        {invoices.recent.length > 0 ? (
          <>
            <h3 className="cp-page-section__title">Recent invoices</h3>
            {invoices.recent.map((invoice) => {
              const cardInvoice = invoiceForCard(invoice, snapshot.projects);
              return (
                <InvoiceListCard
                  key={`recent-${invoice.invoice_id}`}
                  invoice={cardInvoice}
                  showClient={false}
                  onOpen={onOpenInvoice}
                />
              );
            })}
          </>
        ) : null}
      </div>
    </section>
  );
}

function ActivitySection({ snapshot, onOpenProject, onOpenInvoice }) {
  const items = snapshot.recentActivity || [];

  return (
    <section className="cp-page-section" data-testid="client-intel-activity">
      <h2 className="cp-page-section__title">Recent Activity</h2>
      <div className="cp-page-section__body cp-page-data-surface">
        {items.length === 0 ? (
          <p className="client-intel__empty">
            No recent project, invoice, or time activity for this client.
          </p>
        ) : (
          items.map((item) => (
            <ActivityRow
              key={`${item.type}-${item.id}-${item.at}`}
              item={item}
              onOpenProject={onOpenProject}
              onOpenInvoice={onOpenInvoice}
            />
          ))
        )}
      </div>
    </section>
  );
}

/**
 * Internal Client Intelligence / Client 360 detail view.
 * Metrics are derived — never manually maintained duplicates.
 */
export function ClientIntelligenceScreen({
  client,
  onBack,
  onEditClient,
  onOpenProject,
  onOpenInvoice,
  onOpenProjectsForClient,
  onOpenTimeLogsForClient,
}) {
  const { actor, can, CAPABILITY, directReportIds } = usePermissions();
  const canEdit = can(CAPABILITY.CLIENT_EDIT);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mobileTab, setMobileTab] = useState("snapshot");
  const [showCompleted, setShowCompleted] = useState(false);
  const [statementOpen, setStatementOpen] = useState(false);
  const isDesktop = useIsDesktopViewport();
  const canViewStatement = can(CAPABILITY.ACCOUNTS_RECEIVABLE_VIEW);
  const openStatement = canViewStatement ? () => setStatementOpen(true) : null;

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!client?.client_id) {
        setSnapshot(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const next = await loadClientIntelligence(actor, client.client_id, {
          client,
          directReportIds,
        });
        if (!cancelled) {
          setSnapshot(next);
        }
      } catch (loadError) {
        console.error("[ClientIntelligence] load failed:", loadError);
        if (!cancelled) {
          setError("Unable to load client intelligence.");
          setSnapshot(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [actor, client, directReportIds]);

  const emptySnapshot = useMemo(
    () => ({
      client,
      metrics: {
        activeProjectCount: 0,
        completedProjectCount: 0,
        activeProjectValue: null,
        totalHours: null,
        outstandingBalance: null,
        overdueAmount: null,
        openInvoiceCount: null,
        overdueInvoiceCount: null,
        paidInvoiceCount: null,
        accountUpToDate: null,
        hasInvoiceHistory: false,
      },
      projects: { active: [], completed: [], all: [] },
      invoices: { open: [], recent: [] },
      recentActivity: [],
      flags: {
        includeFinancial: false,
        includeHours: false,
        accountUpToDate: false,
      },
    }),
    [client],
  );

  const view = snapshot || emptySnapshot;

  const handleViewProjects = () => {
    onOpenProjectsForClient?.(client);
  };

  const handleOpenTimeLogs = () => {
    onOpenTimeLogsForClient?.(client, view.projects.active);
  };

  if (statementOpen && client?.client_id) {
    return (
      <ClientStatementScreen
        currentUser={actor}
        lockedClientId={client.client_id}
        lockedClientName={client.client_name}
        onBack={() => setStatementOpen(false)}
      />
    );
  }

  return (
    <CrewPilotModuleShell
      title="Client Hub"
      subtitle="Client Intelligence"
      onBack={onBack}
      layout={PAGE_LAYOUT.HUB}
      headerActions={
        canEdit ? (
          <button type="button" style={ACTION_BTN_SM} onClick={onEditClient}>
            Edit
          </button>
        ) : null
      }
    >
      <div className="client-intel cp-page-stack">
        {loading ? (
          <p className="client-intel__empty">Loading client relationship…</p>
        ) : null}
        {error ? <p className="client-intel__empty">{error}</p> : null}

        {isDesktop ? (
          <ClientDesktopDetail
            client={client}
            view={view}
            canEdit={canEdit}
            onEditClient={onEditClient}
            onViewProjects={handleViewProjects}
            onOpenTimeLogs={handleOpenTimeLogs}
            onOpenProject={onOpenProject}
            onOpenInvoice={onOpenInvoice}
            onOpenStatement={openStatement}
          />
        ) : (
          <>
        <div className="client-intel__mobile-only cp-page-stack">
          <IdentityBlock
            client={client}
            canEdit={canEdit}
            onEdit={onEditClient}
            onViewProjects={handleViewProjects}
          />

          <div className="client-intel__tabs" role="tablist" aria-label="Client hub sections">
            {MOBILE_TABS.map((tab) => {
              if (tab.id === "financial" && !view.flags.includeFinancial) {
                return null;
              }
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={mobileTab === tab.id}
                  className="client-intel__tab"
                  style={{
                    ...HUB_STATUS_PILL_BTN_BASE_STYLE,
                    ...(mobileTab === tab.id
                      ? HUB_STATUS_PILL_ACTIVE_GOLD_STYLE
                      : {}),
                  }}
                  onClick={() => setMobileTab(tab.id)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {mobileTab === "snapshot" ? (
            <SnapshotMetrics
              snapshot={view}
              onFocusProjects={() => setMobileTab("projects")}
              onFocusFinancial={() => setMobileTab("financial")}
              onOpenTimeLogs={handleOpenTimeLogs}
            />
          ) : null}
          {mobileTab === "projects" ? (
            <>
              <ProjectsSection
                snapshot={view}
                onOpenProject={onOpenProject}
                showCompleted={showCompleted}
              />
              {view.projects.completed.length > 0 ? (
                <button
                  type="button"
                  style={ACTION_BTN_SM}
                  onClick={() => setShowCompleted((value) => !value)}
                >
                  {showCompleted ? "Hide completed" : "Show completed projects"}
                </button>
              ) : null}
            </>
          ) : null}
          {mobileTab === "financial" ? (
            <FinancialSection
              snapshot={view}
              onOpenInvoice={onOpenInvoice}
              onOpenStatement={openStatement}
            />
          ) : null}
          {mobileTab === "activity" ? (
            <ActivitySection
              snapshot={view}
              onOpenProject={onOpenProject}
              onOpenInvoice={onOpenInvoice}
            />
          ) : null}
        </div>

        <div className="client-intel__desktop-only">
          <div className={PAGE_LAYOUT_CLASS.SPLIT}>
            <div className={PAGE_LAYOUT_CLASS.SPLIT_MAIN}>
              <div className="cp-page-stack">
                <SnapshotMetrics
                  snapshot={view}
                  onFocusProjects={() => setShowCompleted(false)}
                  onFocusFinancial={() => {}}
                  onOpenTimeLogs={handleOpenTimeLogs}
                />
                <ProjectsSection
                  snapshot={view}
                  onOpenProject={onOpenProject}
                  showCompleted={showCompleted}
                />
                {view.projects.completed.length > 0 ? (
                  <button
                    type="button"
                    style={ACTION_BTN_SM}
                    onClick={() => setShowCompleted((value) => !value)}
                  >
                    {showCompleted ? "Hide completed" : "Show completed projects"}
                  </button>
                ) : null}
                <FinancialSection
                  snapshot={view}
                  onOpenInvoice={onOpenInvoice}
                  onOpenStatement={openStatement}
                />
                <ActivitySection
                  snapshot={view}
                  onOpenProject={onOpenProject}
                  onOpenInvoice={onOpenInvoice}
                />
              </div>
            </div>
            <aside className={PAGE_LAYOUT_CLASS.SPLIT_ASIDE}>
              <IdentityBlock
                client={client}
                canEdit={canEdit}
                onEdit={onEditClient}
                onViewProjects={handleViewProjects}
              />
            </aside>
          </div>
        </div>
          </>
        )}
      </div>
    </CrewPilotModuleShell>
  );
}
